import { Database, Zap, Settings, Wrench } from "lucide-react";
import DigitalTwinsSection from "./sections/digital-twins-section";
import DigitalTwinsLifecycle from "./sections/digital-twins-lifecycle";
import DigitalTwinsParameters from "./sections/digital-twins-parameters";
import DigitalTwinsQuickStart from "./sections/digital-twins-quick-start";
import CTASection from "../../components/cta-section";
import { useTranslation } from "../../i18n";

export default function NewDigitalTwinsRoute() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen overflow-x-hidden">
      {/* Gradient Background */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-br from-purple-500 via-indigo-600 to-blue-500 opacity-90" />

      {/* Diagonal White Section */}
      <div className="absolute top-0 left-0 right-0 h-[800px] -z-5">
        <div className="absolute top-0 left-0 right-0 h-full bg-white transform -skew-y-6 origin-top-left translate-y-[30%]" />
      </div>

      {/* Main Content */}
      <main className="relative z-10 px-4 md:px-8 pt-32 pb-24">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h1 className="text-4xl md:text-6xl font-bold text-navy-900 leading-tight">
              {t('routes.digitalTwins.heroTitle')} {" "}
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-violet-600 via-purple-600 to-orange-500">
                {t('routes.digitalTwins.heroHighlight')}
              </span>
            </h1>
            <p className="mt-6 text-gray-700 text-lg">
              {t('routes.digitalTwins.heroDescription')}
            </p>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex items-center">
                <div className="h-10 w-10 flex-shrink-0 mr-3 rounded-full bg-purple-100 flex items-center justify-center">
                  <Database className="h-5 w-5 text-purple-600" />
                </div>
                <span className="text-sm font-medium">
                  {t('routes.digitalTwins.bullets.registry')}
                </span>
              </div>
              <div className="flex items-center">
                <div className="h-10 w-10 flex-shrink-0 mr-3 rounded-full bg-blue-100 flex items-center justify-center">
                  <Zap className="h-5 w-5 text-blue-600" />
                </div>
                <span className="text-sm font-medium">
                  {t('routes.digitalTwins.bullets.automation')}
                </span>
              </div>
              <div className="flex items-center">
                <div className="h-10 w-10 flex-shrink-0 mr-3 rounded-full bg-indigo-100 flex items-center justify-center">
                  <Settings className="h-5 w-5 text-indigo-600" />
                </div>
                <span className="text-sm font-medium">
                  {t('routes.digitalTwins.bullets.edge')}
                </span>
              </div>
            </div>
          </div>

          <div className="flex justify-center">
            <div className="relative">
              {/* Digital Twin Visualization */}
              <div className="w-80 h-96 bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100">
                <div className="h-full bg-gradient-to-br from-purple-50 to-indigo-50 p-6 flex flex-col">
                  <div className="text-center mb-6">
                  <div className="mb-2">
                    <Wrench className="h-12 w-12 text-purple-600" />
                  </div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {t('routes.digitalTwins.dashboardSample.title')}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {t('routes.digitalTwins.dashboardSample.subtitle')}
                    </p>
                  </div>

                  <div className="space-y-4 flex-1">
                    <div className="bg-white p-3 rounded-lg shadow-sm">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">
                          {t('routes.digitalTwins.dashboardSample.batteryLevel')}
                        </span>
                        <span className="text-sm text-green-600">87%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                        <div
                          className="bg-green-500 h-2 rounded-full"
                          style={{ width: "87%" }}
                        ></div>
                      </div>
                    </div>

                    <div className="bg-white p-3 rounded-lg shadow-sm">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">
                          {t('routes.digitalTwins.dashboardSample.doorStatus')}
                        </span>
                        <span className="text-sm text-red-600">Locked</span>
                      </div>
                    </div>

                    <button className="w-full bg-purple-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors">
                      {t('routes.digitalTwins.dashboardSample.unlockDevice')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* JUHUU Digital Twins Main Section */}
      <DigitalTwinsSection />

      {/* Device Lifecycle */}
      <DigitalTwinsLifecycle />

      {/* Parameters */}
      <DigitalTwinsParameters />

      {/* Quick Start Guide */}
      <DigitalTwinsQuickStart />
      <div id="contact">
        <CTASection />
      </div>
    </div>
  );
}
